// @greenlight-spec: 28-it-dashboard-ui.md#compliance--soc-2-evidence
// @greenlight-spec: 10-security-compliance.md#soc-2-evidence-view
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { Link } from 'react-router-dom';

import { usePageTitle } from '@/components/app-shell/AppShell';
import { GdprSection } from '@/components/compliance/GdprSection';
import { HipaaSection } from '@/components/compliance/HipaaSection';
import { buildSoc2Csv, buildSoc2Json } from '@/components/compliance/exportSoc2';
import { Eyebrow } from '@/components/greenlight/Eyebrow';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { PageHeader } from '@/components/greenlight/PageHeader';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { downloadTextFile } from '@/lib/download';
import type { Soc2AppEvidence, Soc2ControlRow, Soc2Evidence } from '@greenlight/contract';

const FAMILY_LABEL: Record<Soc2ControlRow['family'], string> = {
  CC6: 'CC6 · Logical access',
  CC7: 'CC7 · System monitoring and vulnerabilities',
  CC8: 'CC8 · Change management',
};

function ControlRow({ row }: { readonly row: Soc2ControlRow }): React.JSX.Element {
  return (
    <li className="flex flex-col gap-2 py-4 md:flex-row md:gap-6">
      <div className="md:w-32 md:shrink-0">
        <MonoValue>{row.control}</MonoValue>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium text-foreground">{row.title}</p>
        <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{row.mechanism}</p>
        {row.policy_ids.length > 0 && (
          <p className="mt-2 flex flex-wrap gap-1.5">
            {row.policy_ids.map((id) => (
              <MonoValue key={id}>{id}</MonoValue>
            ))}
          </p>
        )}
      </div>
      <div className="flex items-start gap-2 md:w-44 md:shrink-0 md:justify-end">
        {/* Keyed on the org's resolved coverage, not the mapping's kind: the
            code-finding checks ship at warn, so a gate row is only "enforced"
            where this org left every rule behind it at block. */}
        <StatusBadge variant={row.coverage === 'enforced' ? 'active' : 'inactive'}>
          {row.coverage === 'enforced'
            ? 'Enforced at merge'
            : row.coverage === 'reported'
              ? 'Reported, not blocking'
              : 'Recorded'}
        </StatusBadge>
        <Link
          to={row.evidence_href}
          className="text-[12px] font-medium text-link underline-offset-4 hover:underline"
        >
          Evidence
        </Link>
      </div>
    </li>
  );
}

/** Narrowed rather than asserted: `expires_at` is nullable on the contract. */
function ciWindow(expiresAt: string | null): string {
  return expiresAt === null
    ? 'SBOM in CI'
    : `SBOM in CI until ${new Date(expiresAt).toLocaleDateString()}`;
}

function AppEvidenceRow({
  app,
  onDownloadSbom,
}: {
  readonly app: Soc2AppEvidence;
  readonly onDownloadSbom: (app: Soc2AppEvidence) => Promise<void>;
}): React.JSX.Element {
  const gate = app.gate_allowed === null ? 'Not recorded' : app.gate_allowed ? 'Allowed' : 'Denied';
  return (
    <li className="flex flex-col gap-1.5 py-3 md:flex-row md:items-center md:gap-4">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-foreground">{app.app_name}</p>
        <p className="mt-0.5 text-[12px] text-muted-foreground">
          {app.commit_sha === null ? (
            'No deploy recorded yet.'
          ) : (
            <>
              Commit <MonoValue>{app.commit_sha.slice(0, 7)}</MonoValue> · gate {gate}
              {app.pull_request_number !== null && <> · PR #{app.pull_request_number}</>}
            </>
          )}
        </p>
      </div>
      <p className="text-[12px] text-muted-foreground md:w-56 md:text-right">
        {/* An app that never deployed has no SBOM to have lost. Saying one
            lapsed would invent a build that never happened. */}
        {app.run_id === null ? (
          '—'
        ) : app.sbom.source === 'greenlight' && app.sbom.available ? (
          <button
            type="button"
            className="font-medium text-link underline-offset-4 hover:underline"
            onClick={() => void onDownloadSbom(app)}
          >
            Download SBOM
          </button>
        ) : app.sbom.available ? (
          ciWindow(app.sbom.expires_at)
        ) : (
          'SBOM no longer retained'
        )}
      </p>
    </li>
  );
}

function EvidenceView({ evidence }: { readonly evidence: Soc2Evidence }): React.JSX.Element {
  const families: Soc2ControlRow['family'][] = ['CC6', 'CC7', 'CC8'];
  // Signed at click time rather than for every row on load: the URL is
  // short-lived, so a batch signed on render would be stale before it was used.
  //
  // The tab is opened *before* the await, because a window opened after one is
  // no longer attributed to the click and browsers block it — the button would
  // do nothing at all, with no error to explain it. `opener` is cleared straight
  // after, since the handle we need to keep rules out passing `noopener`.
  const downloadSbom = async (app: Soc2AppEvidence): Promise<void> => {
    if (app.run_id === null) return;
    const tab = window.open('', '_blank');
    if (tab === null) {
      // Never fall back to this tab. Retention stores the document with a
      // CycloneDX content type and no Content-Disposition, so the browser is
      // free to render it — navigating here would replace the dashboard with
      // raw JSON and lose the page the auditor was reading.
      toast.error('Allow pop-ups for this site to open the SBOM.');
      return;
    }
    tab.opener = null;
    try {
      const res = await api.compliance.sbom.query({ params: { runId: app.run_id } });
      if (res.status !== 200) {
        tab.close();
        toast.error('That SBOM could not be retrieved.');
        return;
      }
      tab.location.replace(res.body.url);
    } catch {
      tab.close();
      toast.error('That SBOM could not be retrieved.');
    }
  };

  const download = (format: 'json' | 'csv'): void => {
    const stamp = evidence.generated_at.slice(0, 10);
    if (format === 'json') {
      downloadTextFile(
        `greenlight-soc2-${stamp}.json`,
        buildSoc2Json(evidence),
        'application/json',
      );
    } else {
      downloadTextFile(`greenlight-soc2-${stamp}.csv`, buildSoc2Csv(evidence), 'text/csv');
    }
  };

  return (
    <div className="mt-6 space-y-5">
      <SectionCard title="What this covers">
        <p className="text-[13px] leading-relaxed text-muted-foreground" data-testid="scope-note">
          {evidence.scope_note}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="outline" className="h-11 md:h-9" onClick={() => download('json')}>
            Export JSON
          </Button>
          <Button variant="outline" className="h-11 md:h-9" onClick={() => download('csv')}>
            Export CSV
          </Button>
        </div>
      </SectionCard>

      {families.map((family) => {
        const rows = evidence.controls.filter((c) => c.family === family);
        if (rows.length === 0) return null;
        return (
          <SectionCard key={family}>
            <Eyebrow>{FAMILY_LABEL[family]}</Eyebrow>
            <ul className="mt-2 divide-y divide-border">
              {rows.map((row) => (
                <ControlRow key={row.control} row={row} />
              ))}
            </ul>
          </SectionCard>
        );
      })}

      <SectionCard title="Latest deploy per app">
        {evidence.apps.length === 0 ? (
          <p className="text-[13px] text-muted-foreground" data-testid="compliance-no-apps">
            No apps yet, so there is no deploy evidence to show.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {evidence.apps.map((app) => (
              <AppEvidenceRow key={app.app_id} app={app} onDownloadSbom={downloadSbom} />
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

export function CompliancePage(): React.JSX.Element {
  usePageTitle('Compliance');
  const { data, isLoading, isError } = api.compliance.soc2.useQuery(
    ['compliance-soc2'],
    undefined,
    {
      queryKey: ['compliance-soc2'],
    },
  );
  // A second framework rather than a second page: an auditor reads one estate. The
  // HIPAA read failing on its own leaves the SOC 2 view intact, because a missing
  // BAA table is not a reason to withhold control coverage.
  const hipaa = api.compliance.hipaa.useQuery(['compliance-hipaa'], undefined, {
    queryKey: ['compliance-hipaa'],
  });
  const hipaaEvidence = hipaa.data?.status === 200 ? hipaa.data.body : null;
  const hipaaInScope = (hipaaEvidence?.phi_integrations.length ?? 0) > 0;
  const gdpr = api.compliance.gdpr.useQuery(['compliance-gdpr'], undefined, {
    queryKey: ['compliance-gdpr'],
  });
  const gdprEvidence = gdpr.data?.status === 200 ? gdpr.data.body : null;
  const gdprInScope = gdprEvidence?.eu_designated === true;
  const extraFrameworkInScope = hipaaInScope || gdprInScope;

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <PageHeader
        eyebrow="Compliance"
        title={extraFrameworkInScope ? 'Compliance evidence' : 'SOC 2 evidence'}
      />

      {isLoading ? (
        <div className="mt-6 space-y-3" data-testid="compliance-loading">
          <Skeleton className="h-32 w-full rounded-gl-lg" />
          <Skeleton className="h-64 w-full rounded-gl-lg" />
        </div>
      ) : isError || data?.status !== 200 ? (
        <Alert variant="destructive" data-testid="compliance-error">
          <AlertTitle>Couldn't load compliance evidence</AlertTitle>
          <AlertDescription>
            Something went wrong reading the control mapping. Retry in a moment.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <EvidenceView evidence={data.body} />
          {hipaaEvidence !== null && <HipaaSection evidence={hipaaEvidence} />}
          {gdprEvidence !== null && <GdprSection evidence={gdprEvidence} />}
        </>
      )}
    </div>
  );
}
